import {
  asStableCallback,
  useStableCallback,
} from "@bezoned/shared/hooks/use-stable-callback.js";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  InteractionTag,
  InteractionTagPrimary,
  Option,
  Persona,
  Spinner,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  Tooltip,
} from "@fluentui/react-components";
import type { TagPickerOnOptionSelectData } from "@fluentui/react-components";
import { ArrowClockwiseRegular } from "@fluentui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";

import { getOrganizationUsers } from "../../../../../services/api.service.js";
import { recordErrorInSentry } from "../../../../../utils/init-sentry.js";

const DEBOUNCE_DELAY_MS = 300;

export type OrganizationUser = Awaited<
  ReturnType<typeof getOrganizationUsers>
>["users"][number];

interface TeamAddMembersDialogProps {
  open: boolean;
  excludedUserIds: Set<string>;
  onClose: () => void;
  onAddUsers: (users: OrganizationUser[]) => void;
  allowAddAllTeamsUsersAction?: boolean;
  onAddAllTeamsUsers?: () => void;
}

export const TeamAddMembersDialog = ({
  open,
  excludedUserIds,
  onClose,
  onAddUsers,
  allowAddAllTeamsUsersAction = false,
  onAddAllTeamsUsers,
}: TeamAddMembersDialogProps) => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<OrganizationUser[]>([]);
  const [addAllTeamsUsersToOffice, setAddAllTeamsUsersToOffice] =
    useState(false);

  const organizationUsersQuery = useQuery({
    enabled: open && searchTerm.length > 0,
    queryFn: () =>
      getOrganizationUsers({
        search: searchTerm.length ? searchTerm : undefined,
      }),
    queryKey: ["getOrganizationUsers", searchTerm],
  });

  useEffect(() => {
    if (organizationUsersQuery.error) {
      recordErrorInSentry(organizationUsersQuery.error);
    }
  }, [organizationUsersQuery.error]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value.trim());
      }, DEBOUNCE_DELAY_MS),
    []
  );

  useEffect(() => {
    if (!open) {
      debouncedSearch.cancel();
      setSearchInput("");
      setSearchTerm("");
      setSelectedUsers([]);
      setAddAllTeamsUsersToOffice(false);
    }
  }, [debouncedSearch, open]);

  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch]
  );

  const availableUsers = useMemo(() => {
    const users = organizationUsersQuery.data?.users ?? [];
    return users.filter(
      (user) =>
        !(
          excludedUserIds.has(user.id) ||
          selectedUsers.some((selected) => selected.id === user.id)
        )
    );
  }, [excludedUserIds, organizationUsersQuery.data, selectedUsers]);

  const handleTagPickerOptionSelect = (
    _event: unknown,
    data: TagPickerOnOptionSelectData
  ) => {
    if (addAllTeamsUsersToOffice) {
      return;
    }

    const userId = data.value;
    const selectedUser = availableUsers.find((user) => user.id === userId);

    if (!selectedUser) {
      return;
    }

    if (data.selectedOptions.includes(userId)) {
      setSelectedUsers((prev) => {
        if (prev.some((entry) => entry.id === selectedUser.id)) {
          return prev;
        }
        return [...prev, selectedUser];
      });
    } else {
      setSelectedUsers((prev) =>
        prev.filter((user) => user.id !== selectedUser.id)
      );
    }

    setSearchInput("");
    setSearchTerm("");
  };

  const handleAddAllTeamsUsersChange = (checked: boolean) => {
    setAddAllTeamsUsersToOffice(checked);

    if (checked) {
      setSearchInput("");
      setSearchTerm("");
      setSelectedUsers([]);
    }
  };
  const handleClick258 = useStableCallback(() => {
    if (addAllTeamsUsersToOffice) {
      onAddAllTeamsUsers?.();
    } else {
      onAddUsers(selectedUsers);
    }

    onClose();
  });
  const handleChange246 = useStableCallback((_event, data) => {
    handleAddAllTeamsUsersChange(data.checked === true);
  });

  const handleChange213 = useStableCallback((event) => {
    const { value } = event.target;
    setSearchInput(value);
    debouncedSearch(value);
  });

  const handleOpenChange165 = useStableCallback((_event, data) => {
    if (!data.open && selectedUsers.length === 0) {
      onClose();
    }
  });

  return (
    <Dialog onOpenChange={handleOpenChange165} open={open}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Add team members</DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-4">
              <Field label="Search and select users">
                <TagPicker
                  disabled={addAllTeamsUsersToOffice}
                  onOptionSelect={handleTagPickerOptionSelect}
                  selectedOptions={selectedUsers.map((user) => user.id)}
                >
                  <TagPickerControl
                    expandIcon={
                      organizationUsersQuery.isFetching ? (
                        <ArrowClockwiseRegular className="animate-spin" />
                      ) : null
                    }
                  >
                    <TagPickerGroup>
                      {selectedUsers.map((user) => (
                        <InteractionTag
                          appearance="filled"
                          key={user.id}
                          value={user.id}
                        >
                          <InteractionTagPrimary
                            hasSecondaryAction
                            onClick={asStableCallback(() => {
                              setSelectedUsers((prev) =>
                                prev.filter((u) => u.id !== user.id)
                              );
                            })}
                          >
                            {user.displayName} &lt;{user.email}&gt;
                          </InteractionTagPrimary>
                        </InteractionTag>
                      ))}
                    </TagPickerGroup>
                    <TagPickerInput
                      aria-label="Select users"
                      disabled={addAllTeamsUsersToOffice}
                      onChange={handleChange213}
                      placeholder="Type a name or email"
                      value={searchInput}
                    />
                  </TagPickerControl>
                  <TagPickerList>
                    {availableUsers.map((user) => (
                      <Option
                        key={user.id}
                        text={user.displayName}
                        value={user.id}
                      >
                        <Persona
                          name={user.displayName}
                          secondaryText={user.email}
                        />
                      </Option>
                    ))}
                  </TagPickerList>
                </TagPicker>
              </Field>
              {Boolean(allowAddAllTeamsUsersAction) && (
                <Tooltip
                  content="Instead of selecting specific users, add all organization members that use MS Teams to this office."
                  relationship="description"
                >
                  <Checkbox
                    checked={addAllTeamsUsersToOffice}
                    label="Add all MS Teams members"
                    onChange={handleChange246}
                  />
                </Tooltip>
              )}

              {Boolean(organizationUsersQuery.isFetching) && (
                <div className="text-bzd-gray-600 flex items-center gap-2 text-sm">
                  <Spinner size="tiny" />
                  Searching users...
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} type="button">
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!addAllTeamsUsersToOffice && selectedUsers.length === 0}
              onClick={handleClick258}
              type="button"
            >
              {addAllTeamsUsersToOffice ? "Add all members" : "Add members"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
